import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Button, Input, Text } from '@/components/ui';
import { isValidEmail } from '@console/utils';
import { authApi } from '@/lib/auth/api';
import { ArrowLeftIcon, MailIcon } from 'lucide-react';
import { Logo } from '@/assets/images/Logo';
import { toast } from 'sonner';

const createForgotPasswordSchema = (t: any) =>
  z.object({
    email: z.string().email(t('auth.invalidEmail')).refine(isValidEmail, t('auth.invalidEmail')),
  });

type ForgotPasswordForm = z.infer<ReturnType<typeof createForgotPasswordSchema>>;

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const forgotPasswordSchema = createForgotPasswordSchema(t);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  async function onSubmit(data: ForgotPasswordForm) {
    setIsLoading(true);
    try {
      await authApi.forgotPassword(data);
      setIsEmailSent(true);
      toast.success(t('auth.resetPasswordEmailSent'));
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('auth.forgotPasswordError'));
    } finally {
      setIsLoading(false);
    }
  }

  if (isEmailSent) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="bg-base-white border-transparent-dark-12 p-lg flex w-96 flex-col rounded-3xl border shadow-xs">
          <Logo className="self-center" />

          <div className="mt-2xl text-center">
            <div className="mb-md mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <MailIcon className="h-6 w-6 text-green-600" />
            </div>
            <Text variant="label" size="xl" strong className="text-center">
              {t('auth.emailSent')}
            </Text>
            <Text className="text-neutral-muted mt-2xs">
              {t('auth.resetPasswordEmailSentTo', { email: getValues('email') })}
            </Text>
          </div>

          <Link to="/login">
            <Button variant="secondary" className="mt-2xl w-full">
              <ArrowLeftIcon size={16} />
              {t('auth.backToLogin')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="bg-base-white border-transparent-dark-12 p-lg flex w-96 flex-col rounded-3xl border shadow-xs">
        <Logo className="self-center" />

        <div className="mt-2xl">
          <Text variant="label" size="xl" strong className="text-center">
            {t('auth.forgotPassword')}
          </Text>
          <Text className="text-neutral-muted mt-2xs text-center">
            {t('auth.forgotPasswordDescription')}
          </Text>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-xl flex flex-col" noValidate>
          <Input
            {...register('email')}
            type="email"
            label={t('auth.email')}
            placeholder={t('auth.emailPlaceholder')}
            error={errors.email?.message as string}
            disabled={isLoading}
          />

          <Button type="submit" className="mt-2xl" loading={isLoading}>
            {t('auth.sendResetPasswordEmail')}
          </Button>

          <Link to="/login">
            <Text
              variant="label"
              strong
              className="text-neutral-muted mt-md hover:text-neutral-default gap-2xs flex cursor-pointer items-center justify-center transition-colors duration-150 ease-in-out"
              role="button"
            >
              <ArrowLeftIcon size={16} />
              {t('auth.backToLogin')}
            </Text>
          </Link>
        </form>
      </div>
    </div>
  );
}
